import React, { useState } from 'react'
import Linkify from 'react-linkify'
import classNames from 'classnames'

import { regexEscape } from 'lib/utils/regexManager'
import i18n from 'lib/i18n'
import { renderWhatsAppFormatted } from 'lib/utils/whatsappFormatter'
import { openUrlWrapperForFlutterApp } from 'lib/config/appBridge'

import styles from '../Text.module.scss'

const RenderChild = ({
  children,
  translateProps,
  translate,
  truncate,
  size,
  highlightText,
  linkify,
  fontSize,
  whatsappFormat,
}: {
  children: React.ReactNode
  translateProps: Record<string, string>
  translate: boolean
  truncate: boolean
  size: number
  highlightText: string
  linkify: boolean
  fontSize: string
  whatsappFormat: boolean
}): React.ReactElement => {
  let finalText = (children as string) || (children as React.ReactElement)
  const [isReadMore, setReadMore] = useState(truncate)

  const toggleReadMore = () => setReadMore((prev) => !prev)

  const buttonClass = classNames(styles.readMoreButton, {
    [styles[fontSize]]: fontSize,
  })

  const linkifyComponentDecorator = (
    href: string,
    text: string,
    key: string
  ) => (
    <a
      href={href}
      key={key}
      target="_blank"
      rel="noreferrer"
      className={styles.link}
      onClick={openUrlWrapperForFlutterApp}
    >
      {text}
    </a>
  )

  const highlightTextFn = (text: string, highlightText: string) => {
    if (!highlightText || typeof text !== 'string') return text

    const regex = new RegExp(`(${regexEscape(highlightText)})`, 'gi')
    const normalizedHighlight = highlightText.toLowerCase()

    const parts = text
      .split(regex)
      .map((part, index) => (
        <React.Fragment key={index}>
          {part.toLowerCase() === normalizedHighlight ? (
            <span className={styles.highlight}>{part}</span>
          ) : (
            part
          )}
        </React.Fragment>
      ))

    return <>{parts}</>
  }

  const readMoreText = (text: string, size: number) => {
    return `${text.slice(0, size)}...`
  }

  if (translate) {
    try {
      finalText = i18n?.t?.(finalText as string, translateProps)
    } catch (error) {
      return finalText as React.ReactElement
    }
  }

  if (truncate && typeof finalText === 'string' && finalText?.length > size) {
    if (isReadMore) {
      finalText = (
        <>
          {readMoreText(finalText, size)}
          {
            <button className={buttonClass} onClick={toggleReadMore}>
              Read More
            </button>
          }
        </>
      )
    } else {
      finalText = (
        <>
          {finalText}
          <button className={buttonClass} onClick={toggleReadMore}>
            Read Less
          </button>
        </>
      )
    }
  }

  // WhatsApp format must run BEFORE linkify to parse the string first
  if (whatsappFormat && typeof finalText === 'string') {
    finalText = renderWhatsAppFormatted(finalText) as React.ReactElement
  }

  if (highlightText && typeof finalText === 'string') {
    finalText = highlightTextFn(finalText as string, highlightText)
  }

  if (linkify) {
    const linkifyChildren = React.Children.toArray(
      finalText === undefined || finalText === null ? '' : finalText
    )
    finalText = (
      <Linkify
        componentDecorator={(href: string, text: string, key: number) =>
          linkifyComponentDecorator(href, text, key.toString())
        }
      >
        {linkifyChildren}
      </Linkify>
    )
  }

  return (finalText || '') as React.ReactElement
}

export default RenderChild
